import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LocaleTabs } from "@/components/LocaleTabs";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Field, Input, Select } from "@/components/ui/field";
import { sessionQuery } from "@/features/auth/queries";
import { useSession } from "@/features/auth/useSession";
import { masterTypeIndexQuery } from "@/features/master-types/queries";
import { createMasterType } from "@/features/schema/api";
import { ApiError, formErrorsFrom } from "@/lib/api/problem";

/**
 * A master type has to exist before a schema can be drafted against it,
 * so this is the first step of the schema builder rather than a separate
 * feature.
 *
 * `parent_code` is set here and nowhere else in this UI: reparenting is
 * its own API route with its own guard (a type with records cannot be
 * moved), and folding it into an edit form would offer a change that is
 * usually refused.
 */
export const Route = createFileRoute("/_authed/master-types/new")({
  staticData: {
    crumbs: [{ key: "nav.masterTypes", to: "/master-types" }, { key: "nav.breadcrumb.new" }],
  },
  loader: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(sessionQuery);
    await context.queryClient.ensureQueryData(masterTypeIndexQuery(me.resolved_locale));
  },
  component: NewMasterType,
});

function NewMasterType() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useSession();
  const locale = me.resolved_locale;

  const { data: index } = useSuspenseQuery(masterTypeIndexQuery(locale));

  const [code, setCode] = useState("");
  const [names, setNames] = useState<Record<string, string>>({});
  const [parent, setParent] = useState("");
  const [tab, setTab] = useState(me.tenant.default_locale);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: () =>
      createMasterType({
        code: code.trim(),
        display_name: Object.fromEntries(
          Object.entries(names).filter(([, value]) => value.trim() !== ""),
        ),
        ...(parent === "" ? {} : { parent_code: parent }),
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["master-types"] });
      void navigate({ to: "/master-types/$code/schema", params: { code: created.code } });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(
          Object.fromEntries(formErrorsFrom(error.problem).map((one) => [one.path, one.message])),
        );
      }
    },
  });

  const defaultLocale = me.tenant.default_locale;
  const nameMissing = (names[defaultLocale] ?? "").trim() === "";

  return (
    <>
      <PageHeader title={t("schema.createType.title")} />
      <form
        noValidate
        className="max-w-xl space-y-5 rounded-panel border border-border bg-surface p-4 sm:p-6"
        onSubmit={(event) => {
          event.preventDefault();
          setErrors({});
          create.mutate();
        }}
      >
        <Field
          id="field-code"
          label={t("schema.createType.code")}
          required
          requiredLabel={t("common.required")}
          hint={t("schema.createType.codeHint")}
          {...(errors.code === undefined ? {} : { error: errors.code })}
        >
          {(aria) => (
            <Input
              {...aria}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              // The server's own CHECK is `^[a-z][a-z0-9_-]*$`; matching
              // it here means the refusal arrives while typing rather
              // than after a round trip.
              pattern="[a-z][a-z0-9_-]*"
            />
          )}
        </Field>

        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium text-foreground">
            {t("schema.createType.name")}
            <span className="ml-1 font-normal text-muted">({t("common.required")})</span>
          </legend>
          <LocaleTabs
            locales={[...me.tenant.locales]}
            defaultLocale={defaultLocale}
            value={tab}
            onValueChange={setTab}
            invalidLocales={nameMissing && errors.display_name !== undefined ? [defaultLocale] : []}
            requiredLabel={t("common.required")}
          >
            {(tabLocale) => (
              <Input
                aria-label={`${t("schema.createType.name")} (${tabLocale})`}
                value={names[tabLocale] ?? ""}
                onChange={(event) =>
                  setNames((current) => ({ ...current, [tabLocale]: event.target.value }))
                }
              />
            )}
          </LocaleTabs>
          {errors.display_name !== undefined && (
            <p aria-live="polite" className="text-xs text-danger">
              {errors.display_name}
            </p>
          )}
        </fieldset>

        <Field
          id="field-parent"
          label={t("schema.createType.parent")}
          requiredLabel={t("common.required")}
        >
          {(aria) => (
            <Select
              {...aria}
              tooltip={t("tooltips.parentType")}
              value={parent}
              onChange={(event) => setParent(event.target.value)}
            >
              <option value="">{t("schema.createType.noParent")}</option>
              {index.all.map((type) => (
                <option key={type.id} value={type.code}>
                  {type.display_name[locale] ?? type.code}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            disabled={create.isPending || code.trim() === "" || nameMissing}
            tooltip={t("tooltips.createType")}
          >
            {t("schema.createType.create")}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void navigate({ to: "/master-types" })}
            tooltip={t("tooltips.cancel")}
          >
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </>
  );
}
